import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, repeat, retry } from 'rxjs/operators';
import { of } from 'rxjs';
import { MydocumentsExportService } from '../core/http/adapters/mydocuments-export/mydocuments-export.service';
import {
  getAllAvailableExportSettings,
  getAllAvailableExportSettingsFailure,
  getAllAvailableExportSettingsSuccess,
  getAvailableCadTypes,
  getAvailableCadTypesFailure,
  getAvailableCadTypesSuccess,
  getAvailableDocumentTypes,
  getAvailableDocumentTypesFailure,
  getAvailableDocumentTypesSuccess,
  getExportSettings,
  getExportSettingsFailure,
  getExportSettingsSuccess,
  getZipSettingsForSession,
  getZipSettingsForSessionFailure,
  getZipSettingsForSessionSuccess,
  postExportSettings,
  postExportSettingsFailure,
  postExportSettingsSuccess,
  postSaveExportConfiguration,
  postSaveExportConfigurationFailure,
  postSaveExportConfigurationSuccess,
  postSaveUserEntries,
  postSaveUserEntriesFailure,
  postSaveUserEntriesSuccess,
} from './mydoc.action';
import { getValue, SOVerify } from '../shared/utility/verify.lib';
import { MydocumentsItemAdapterService } from '../core/http/adapters/mydocuments-item/mydocuments-item.service';
import { ICadType } from '../models/document/cad-type.model';

@Injectable()
export class MydocEffects {
  private readonly retryDelayTime: number = 1000;
  private readonly retryCount: number = 1;

  public getExportSettingsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getExportSettings),
      mergeMap((action) =>
        this.mydocumentsExportService
          .getExportSettings(action.sessionId, action.orderCode)
          .pipe(
            retry({ delay: this.retryDelayTime, count: this.retryCount }),
            map((response) =>
              getExportSettingsSuccess({
                settingsList: getValue(() => response.value, []),
              })
            ),
            catchError((error) => of(getExportSettingsFailure({ error })))
          )
      )
    )
  );

  public getAllAvailableExportSettingsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAllAvailableExportSettings),
      mergeMap((action) =>
        this.mydocumentsExportService
          .getAllAvailableExportSettings(action.sessionId, action.orderCode)
          .pipe(
            retry({ delay: this.retryDelayTime, count: this.retryCount }),
            map((response) =>
              getAllAvailableExportSettingsSuccess({
                configuration: getValue(() => response.value[0], []),
              })
            ),
            catchError((error) =>
              of(getAllAvailableExportSettingsFailure({ error }))
            )
          )
      )
    )
  );

  public getAvailableDocumentTypesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAvailableDocumentTypes),
      mergeMap((action) =>
        this.mydocumentsItemService
          .getDocumentTypeOrder(action.languageId)
          .pipe(
            retry({ delay: this.retryDelayTime, count: this.retryCount }),
            map((documentTypes) =>
              getAvailableDocumentTypesSuccess({ documentTypes: documentTypes })
            ),
            catchError((error) =>
              of(getAvailableDocumentTypesFailure({ error }))
            )
          )
      )
    )
  );

  public getAvailableCadTypesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAvailableCadTypes),
      mergeMap((action) =>
        this.mydocumentsItemService
          .getCatFileDocumentTypes(action.languageId)
          .pipe(
            retry({ delay: this.retryDelayTime, count: this.retryCount }),
            map((response) => {
              const newCadTypes: ICadType[] = SOVerify.getValueFromStructure(
                response,
                'value',
                0,
                'subTypes'
              );
              return getAvailableCadTypesSuccess({ cadTypes: newCadTypes });
            }),
            catchError((error) => of(getAvailableCadTypesFailure({ error })))
          )
      )
    )
  );

  public getZipSettingsForSessionEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getZipSettingsForSession),
      mergeMap((action) =>
        this.mydocumentsExportService
          .getZipSettingsForSession(action.sessionId, action.orderCode)
          .pipe(
            retry({ delay: this.retryDelayTime, count: this.retryCount }),
            map((response) =>
              getZipSettingsForSessionSuccess({
                configuration: getValue(() => response.value[0], []),
              })
            ),
            catchError((error) =>
              of(getZipSettingsForSessionFailure({ error }))
            )
          )
      )
    )
  );

  public postExportSettingsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postExportSettings),
      mergeMap((action) =>
        this.mydocumentsExportService
          .postExportSettings(action.sessionId, action.orderCode)
          .pipe(
            retry({ delay: this.retryDelayTime, count: this.retryCount }),
            map(() => postExportSettingsSuccess()),
            catchError((error) => of(postExportSettingsFailure({ error })))
          )
      )
    )
  );

  public postSaveExportConfigurationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postSaveExportConfiguration),
      mergeMap((action) =>
        this.mydocumentsExportService
          .postSaveUserDefaultSettings(
            action.sessionId,
            action.orderNo,
            action.saveAsDefault,
            action.configuration
          )
          .pipe(
            retry({ delay: this.retryDelayTime, count: this.retryCount }),
            map(() => postSaveExportConfigurationSuccess()),
            catchError((error) =>
              of(postSaveExportConfigurationFailure({ error }))
            )
          )
      )
    )
  );

  public postSaveUserEntriesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postSaveUserEntries),
      mergeMap((action) =>
        this.mydocumentsItemService
          .postSaveUserEntriesV2(
            action.sessionId,
            action.orderNo,
            action.docLanguages,
            action.selectedProducts,
            action.documentTypes,
            action.documentSubTypes
          )
          .pipe(
            retry({ delay: this.retryDelayTime, count: this.retryCount }),
            map(() => postSaveUserEntriesSuccess()),
            catchError((error) => of(postSaveUserEntriesFailure({ error })))
          )
      )
    )
  );

  public constructor(
    private actions$: Actions,
    private mydocumentsExportService: MydocumentsExportService,
    private mydocumentsItemService: MydocumentsItemAdapterService
  ) {}
}
